import React, { useCallback, useMemo, useRef } from 'react';
import { Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import L from 'leaflet';
import type { WildlifeMapObservation } from '../../hooks/useWildlifeLayer';

interface ClusteredWildlifeMarkersProps {
  wildlife: WildlifeMapObservation[];
  onInteract: () => void;
}

const speciesEmoji = (species: string): string => {
  const s = species.toLowerCase();
  if (s.includes('elg') || s.includes('hjort') || s.includes('radyr') || s.includes('rein')) return '🦌';
  if (s.includes('rev')) return '🦊';
  if (s.includes('bjorn') || s.includes('bjørn')) return '🐻';
  if (s.includes('gaupe')) return '🐱';
  if (s.includes('ulv')) return '🐺';
  if (s.includes('oter')) return '🦦';
  if (s.includes('hare')) return '🐇';
  if (s.includes('ørn') || s.includes('hauk') || s.includes('falk')) return '🦅';
  if (s.includes('ugle')) return '🦉';
  if (s.includes('sel')) return '🦭';
  if (s.includes('hval') || s.includes('nise') || s.includes('spekkhogger')) return '🐋';
  return '🐾';
};

// Module-level icon cache: stable L.DivIcon references across renders. Without
// this, every render builds a fresh icon → Leaflet rebuilds the marker DOM →
// any open popup closes. That's the original "blinking" bug.
const ICON_SIZE = 28;
const ICON_HALF = ICON_SIZE / 2;
const wildlifeIconCache = new Map<string, L.DivIcon>();

const wildlifeMarkerIcon = (species: string, visualOffsetX: number = 0): L.DivIcon => {
  const cacheKey = `${species}|${visualOffsetX}`;
  const cached = wildlifeIconCache.get(cacheKey);
  if (cached) return cached;
  const emoji = speciesEmoji(species);
  const icon = L.divIcon({
    className: 'wildlife-marker',
    html: `<div style="width:${ICON_SIZE}px;height:${ICON_SIZE}px;background:#4338ca;border:2px solid white;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${emoji}</div>`,
    iconSize: [ICON_SIZE, ICON_SIZE],
    iconAnchor: [ICON_HALF - visualOffsetX, ICON_HALF],
    popupAnchor: [visualOffsetX, -ICON_HALF],
  });
  wildlifeIconCache.set(cacheKey, icon);
  return icon;
};

const wildlifeClusterIconCache = new Map<number, L.DivIcon>();
const wildlifeClusterIcon = (count: number): L.DivIcon => {
  const cached = wildlifeClusterIconCache.get(count);
  if (cached) return cached;
  const icon = L.divIcon({
    className: 'wildlife-cluster',
    html: `<div style="width:34px;height:34px;background:#4338ca;border:2px solid white;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:11px;font-weight:900;box-shadow:0 2px 8px rgba(0,0,0,0.3);">🐾${count}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });
  wildlifeClusterIconCache.set(count, icon);
  return icon;
};

// ~11m precision is enough to detect "same place" observations from citizen-
// science sources (which often round to the same coordinate).
const getSamePlaceKey = (w: WildlifeMapObservation) =>
  `${w.location.latitude.toFixed(4)}:${w.location.longitude.toFixed(4)}`;

const SPACING_X = 32; // 28px icon + 4px gap

const computeVisualOffset = (indexInGroup: number, groupSize: number): number => {
  if (groupSize <= 1) return 0;
  const center = (groupSize - 1) / 2;
  return (indexInGroup - center) * SPACING_X;
};

// Per-marker child component. Wrapped in React.memo so that when the parent
// re-renders (e.g. routeContextGeometry updates while following a route), the
// individual Marker components only re-render if their own observation actually
// changed. Without this, the parent passes new `position` arrays and new
// `eventHandlers` objects on every render, which makes react-leaflet call
// Leaflet's setLatLng / rebind handlers — that fires 'move' events into
// MarkerClusterGroup which can re-cluster the marker and close any open popup.
// That's the "blinking along a route" symptom.
interface WildlifeMarkerItemProps {
  observation: WildlifeMapObservation;
  visualOffsetX: number;
  onClick: (e: L.LeafletMouseEvent) => void;
  t: TFunction;
  locale: string;
}

const WildlifeMarkerItem: React.FC<WildlifeMarkerItemProps> = React.memo(({
  observation: w, visualOffsetX, onClick, t, locale,
}) => {
  const position = useMemo<[number, number]>(
    () => [w.location.latitude, w.location.longitude],
    [w.location.latitude, w.location.longitude],
  );
  const eventHandlers = useMemo(() => ({ click: onClick }), [onClick]);
  const icon = wildlifeMarkerIcon(w.species, visualOffsetX);

  return (
    <Marker
      position={position}
      icon={icon}
      zIndexOffset={70}
      eventHandlers={eventHandlers}
    >
      <Popup className="custom-popup">
        <div className="p-3 space-y-2 min-w-[240px] max-w-[280px]">
          <div className="flex items-center gap-2 text-violet-600 font-black text-3xs uppercase tracking-widest">
            {t('map.wildlife.single')}
          </div>
          <div className="text-stone-900 font-black text-sm leading-tight">{w.species}</div>
          <div className="text-2xs text-[var(--text-muted)] font-bold">
            {t('map.wildlife.last_seen', { date: w.observedAt ? w.observedAt.toLocaleDateString(locale) : t('map.wildlife.unknown_date') })}
          </div>
          {!!w.observedCount && (
            <div className="text-2xs text-[var(--text-muted)]">{t('map.wildlife.count_seen')} <span className="font-bold">{w.observedCount}</span></div>
          )}
          {(w.locality || w.municipality || w.county) && (
            <div className="text-2xs text-[var(--text-muted)]">
              {t('map.wildlife.location')} {[w.locality, w.municipality, w.county].filter(Boolean).join(', ')}
            </div>
          )}
          {!!w.behavior && (
            <div className="text-2xs text-[var(--text-muted)]">{t('map.wildlife.behavior')} <span className="font-semibold">{w.behavior}</span></div>
          )}
          {!!w.habitat && (
            <div className="text-2xs text-[var(--text-muted)]">{t('map.wildlife.habitat')} <span className="font-semibold">{w.habitat}</span></div>
          )}
          {!!w.precisionMeters && (
            <div className="text-2xs text-[var(--text-muted)]">{t('map.wildlife.precision', { meters: w.precisionMeters })}</div>
          )}
          {w.thumbnailUrl && (
            <img src={w.thumbnailUrl} alt={`${t('map.wildlife.observation_of')} ${w.species}`} className="w-full h-28 object-cover rounded-lg border border-stone-200" loading="lazy" />
          )}
          {(w.datasetName || w.institution) && (
            <div className="text-3xs text-[var(--text-muted)]">{t('map.wildlife.source')} {[w.datasetName, w.institution].filter(Boolean).join(' - ')}</div>
          )}
          {w.detailUrl && (
            <a href={w.detailUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-2xs font-black text-violet-700 hover:underline">
              {t('map.wildlife.view_source')}
            </a>
          )}
          <p className="text-3xs text-[var(--text-muted)] leading-relaxed">{t('map.wildlife.warning')}</p>
        </div>
      </Popup>
    </Marker>
  );
});

WildlifeMarkerItem.displayName = 'WildlifeMarkerItem';

const ClusteredWildlifeMarkers: React.FC<ClusteredWildlifeMarkersProps> = ({ wildlife, onInteract }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'no' ? 'no-NO' : 'en-US';

  // Map.tsx recreates `handleLayerInteraction` on every render. Pin it through
  // a ref so the click handler we pass to children has a stable reference,
  // which lets React.memo on WildlifeMarkerItem actually skip re-renders.
  const onInteractRef = useRef(onInteract);
  onInteractRef.current = onInteract;
  const handleClick = useCallback((e: L.LeafletMouseEvent) => {
    L.DomEvent.stop(e);
    onInteractRef.current();
  }, []);

  const layout = useMemo(() => {
    const safe = wildlife.filter(
      (w) => !!w?.location && Number.isFinite(w.location.latitude) && Number.isFinite(w.location.longitude),
    );
    const groupCounts = new Map<string, number>();
    const indexInGroup: number[] = new Array(safe.length);
    for (let i = 0; i < safe.length; i += 1) {
      const key = getSamePlaceKey(safe[i]);
      const current = groupCounts.get(key) ?? 0;
      indexInGroup[i] = current;
      groupCounts.set(key, current + 1);
    }
    const groupSize: number[] = new Array(safe.length);
    for (let i = 0; i < safe.length; i += 1) {
      groupSize[i] = groupCounts.get(getSamePlaceKey(safe[i])) ?? 1;
    }
    return { safe, indexInGroup, groupSize };
  }, [wildlife]);

  if (layout.safe.length === 0) return null;

  return (
    <MarkerClusterGroup
      chunkedLoading
      maxClusterRadius={45}
      showCoverageOnHover={false}
      disableClusteringAtZoom={18}
      spiderfyOnMaxZoom
      iconCreateFunction={(cluster: any) => wildlifeClusterIcon(cluster.getChildCount())}
    >
      {layout.safe.map((w, idx) => (
        <WildlifeMarkerItem
          key={`wildlife-${w.id}`}
          observation={w}
          visualOffsetX={computeVisualOffset(layout.indexInGroup[idx], layout.groupSize[idx])}
          onClick={handleClick}
          t={t}
          locale={locale}
        />
      ))}
    </MarkerClusterGroup>
  );
};

export default ClusteredWildlifeMarkers;
